import type { Entity } from '@backstage/catalog-model';

export type WorkflowGroup = {
  id: string;
  title: string;
  filter: (entity: Entity) => boolean;
};

export const WORKFLOW_GROUP_ANNOTATION = 'cathaypacific.com/workflow-group';

function groupName(entity: Entity): string | undefined {
  return (
    entity.metadata.annotations?.[
      WORKFLOW_GROUP_ANNOTATION
    ]?.trim().toLowerCase() || undefined
  );
}

function groupTitle(name: string): string {
  return (
    name
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map(word => {
        if (word === 'devops') return 'DevOps';
        if (word === 'ai') return 'AI';
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ') || name
  );
}

export function getWorkflowGroups(
  entities: Entity[],
  configuredGroups: WorkflowGroup[] = [],
): WorkflowGroup[] {
  const defaults = configuredGroups.map(group => ({
    ...group,
    filter: (entity: Entity) => {
      const name = groupName(entity);
      return group.filter(
        name
          ? {
              ...entity,
              metadata: {
                ...entity.metadata,
                annotations: {
                  ...entity.metadata.annotations,
                  [WORKFLOW_GROUP_ANNOTATION]: name,
                },
              },
            }
          : entity,
      );
    },
  }));
  const matchesDefault = (entity: Entity) =>
    defaults.some(group => group.filter(entity));
  const templates = entities.filter(
    entity =>
      entity.kind.toLowerCase() === 'template' && !matchesDefault(entity),
  );
  const names = Array.from(new Set(templates.map(groupName)))
    .filter((name): name is string => !!name)
    .sort((a, b) => a.localeCompare(b));
  const groups: WorkflowGroup[] = [
    ...defaults,
    ...names.map(name => ({
      id: `annotation:${name}`,
      title: groupTitle(name),
      filter: (entity: Entity) =>
        !matchesDefault(entity) && groupName(entity) === name,
    })),
  ];
  if (templates.some(entity => !groupName(entity))) {
    groups.push({
      id: 'uncategorized',
      title: 'Uncategorized',
      filter: entity => !matchesDefault(entity) && !groupName(entity),
    });
  }
  return groups;
}
